<template>
  <div>
    <span class="d-flex row mt-3 justify-content-between">
      <h2 class="mt-0 mb-0 col-5 pl-0 pt-1">Leaderboard</h2>
      <a v-if="!user.id" href="/challenges" class="btn btn-primary__white">My progress</a>
    </span>
    <ul
      v-if="Object.keys(athleteTotalsGrouped).length > 1"
      class="row mt-2"
    >
      <li>
        <button class="active btn btn-filter" @click="switchFilter($event, 'all')">
          Overall
        </button>
      </li>
      <li
        v-for="key in Object.keys(athleteTotalsGrouped)"
        :key="key"
      >
        <button class="text-capitalize btn btn-filter" @click="switchFilter($event, key)">
          {{ key }}
        </button>
      </li>
    </ul>
    <ul class="bar-list mt-2">
      <li
        v-for="(item, index) in totals.slice(0, 10)"
        :key="item.athlete"
        class="row mb-3"
      >
        <span class="w-100">
          <h3 class="athlete-goal p-1 text-right text-muted col-12 mb-0 mt-0 row justify-content-between">
            <span class="row">
              {{ item.athlete }}
              <svg-icon
                v-if="index === 0"
                icon="medal"
                fill-color="gold"
                width="20px"
                height="20px"
                classes="pl-1"
              />
              <svg-icon
                v-if="index === 1"
                icon="medal"
                fill-color="silver"
                width="20px"
                height="20px"
                classes="pl-1"
              />
              <svg-icon
                v-if="index === 2"
                icon="medal"
                fill-color="peru"
                width="20px"
                height="20px"
                classes="pl-1"
              />
            </span>
            <span>
              <strong>
                {{ formatTotal(item) }}</strong> out of <strong>{{ challenge.challenge_type === 'elevation' ? formatNumberWithCommas(item.goal) : item.goal }}
                </strong>
              {{ formatUnit() }}
            </span>
          </h3>
          <span class="row bar col-12 pl-0 pr-0">
            <span class="bar-deets">{{ item.progress >= 100 ? "100%" : parseFloat(item.progress).toFixed(2) + "%" }}</span>
            <span v-if="item.progress < 100" :class="['bar-target', item.onTarget ? 'on-target' : 'off-target']">
              {{ item.onTarget ? 'On target' : 'Keep trying' }}
            </span>
            <span class="bar-width d-block" :style="{ width: item.progress + '%' }"></span>
          </span>
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import SvgIcon from './svg-icon.vue';
import { numberWithCommas, getUnit, getTotal } from '../composables/useFormatters.js';
import { athleteTotals as calcAthleteTotals, sortByDesc, groupByProperty, applyOverrideDistances } from '../composables/useAthleteData.js';

const MI = 1609.344;
const FT = 3.28084;

const props = defineProps({
  activities: {
    type: Array,
    required: true,
  },
  athletes: {
    type: Array,
    required: false,
    default: () => [],
  },
  week: {
    type: Array,
    required: false,
    default: () => [],
  },
  goals: {
    type: Array,
    required: true,
  },
  challenge: {
    type: Object,
    required: true,
  },
  user: {
    type: Object,
    required: true,
  },
  goal: {
    type: [Number, Boolean],
    required: false,
    default: false,
  },
});

const athleteTotalsAll = ref([]);
const athleteTotalsGrouped = ref({});
const totals = ref([]);

const today = new Date();
const oneDay = 1000 * 60 * 60 * 24;
const daysLeft = Math.ceil((new Date(props.challenge.ends).getTime() - today.getTime()) / oneDay);
const percDaysLeft = (daysLeft / 365) * 100;

onMounted(() => {
  // Apply override distances
  applyOverrideDistances([...props.activities]);

  // Calculate athlete totals
  athleteTotalsAll.value = calcAthleteTotals(props.activities);

  // Process each athlete's totals
  for (const athlete of athleteTotalsAll.value) {
    doTotals(athlete);
  }

  // Group activities by type
  const activitiesGrouped = groupByProperty([...props.activities], 'activity_type');

  for (const group in activitiesGrouped) {
    athleteTotalsGrouped.value[group] = calcAthleteTotals(activitiesGrouped[group]);
    for (const athlete of athleteTotalsGrouped.value[group]) {
      doTotals(athlete);
    }
    athleteTotalsGrouped.value[group] = sortByDesc(athleteTotalsGrouped.value[group], 'progress');
  }

  athleteTotalsAll.value = sortByDesc(athleteTotalsAll.value, 'progress');
  totals.value = athleteTotalsAll.value;
});

function doTotals(athlete) {
  const totalMi = athlete.distance / MI;
  const totalEl = athlete.elevation * FT;
  const totalTi = athlete.time / 60;

  const goalObj = props.goals.find(o => o.athlete === athlete.athlete);
  const goal = goalObj ? goalObj.goal : props.challenge.default_goal;

  athlete.goal = goal;

  switch (props.challenge.challenge_type) {
  case 'distance':
    athlete.progress = (totalMi / goal) * 100;
    break;
  case 'elevation':
    athlete.progress = (totalEl / goal) * 100;
    break;
  case 'time':
    athlete.progress = (totalTi / goal) * 100;
    break;
  }

  athlete.onTarget = percDaysLeft > (100 - athlete.progress);
}

function switchFilter(e, filter) {
  e.target.parentElement.parentElement.querySelector('.active').classList.remove('active');
  e.target.classList.add('active');

  if (filter === 'all') {
    totals.value = athleteTotalsAll.value;
  } else {
    totals.value = athleteTotalsGrouped.value[filter];
  }
}

function formatUnit() {
  return getUnit(props.challenge.challenge_type);
}

function formatTotal(item) {
  return getTotal(item, props.challenge.challenge_type);
}

function formatNumberWithCommas(n) {
  return numberWithCommas(n);
}
</script>

<style lang="scss" scoped>
.athlete-name {
  text-transform: capitalize;
}

.bar {
  position: relative;
  background: white;
  border-radius: 25px;
  overflow: hidden;
  .bar-name, .bar-miles {
    position: absolute;
    left: 48%;
    top: 12px;
    text-transform: capitalize;
    color: #243546;
    font-weight: 700;
  }
  .bar-deets {
    position: absolute;
    right: 2px;
    top: 3px;
    text-transform: capitalize;
    color: #174776;
    display: inline-block;
    background: rgba(255, 255, 255, 0.8);
    padding: 6px;
    border-radius: 16px;
    font-size: 14px;
  }
  .bar-name {
    right: auto;
    left: 3px;
    top: 6px;
    font-weight: 500;
    background: #ffffff;
    padding: 5px 10px;
    border-radius: 4px;
    border: 1px solid #d3e3ef
  }
  .bar-miles {
    left: auto;
    right: 3px;
    font-weight: 500;
  }
  .bar-width {
    background-color: #174776;
    height: 31px;
    border-radius: 25px;
  }
}

.bar-target {
  position: absolute;
  left: 3px;
  top: 3px;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;

  &.on-target {
    color: #02a517
  }
}

.bar-digits {
  color: #243546;
  font-weight: 700;
  height: 40px;
  padding-top: 12px;
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  color: white;
}

.mvp {
  color: #02a517;
}
</style>
